import type { BatchExtractionStatus, BatchExtractionStatusCategory } from "@/services/api";

import {
  dqxHasHardFailedRun,
  getPipelineFileDqxState,
  isMultiTableDqxState,
  normalizePipelineDqxState,
  type PipelineDqxPhase,
  type PipelineFileDqxState,
} from "@/lib/pipelineDqx";
import { resolveUploaderEmail } from "@/lib/userAvatar";
export type { PipelineDqxPhase, PipelineFileDqxState };

export type PipelineStageId = "upload" | "map" | "validate" | "resolve" | "published";

export type PipelineStatusVariant =
  | "queued"
  | "mapping"
  | "validating"
  | "errors"
  | "ready"
  | "published";

export interface PipelineFileCardModel {
  fileId: string;

  fileName: string;

  schemaName: string;

  /** Provider id the file was uploaded for; null when unassigned. */
  providerId?: string | null;

  /** Display label for the provider pill (resolved name, raw id, or Unassigned). */
  providerLabel: string;

  /** Uploader Entra oid (or legacy email). */
  uploadedBy?: string | null;

  /** Resolved uploader email for avatar letter (may be null). */
  uploaderEmail?: string | null;

  rowCount?: number | null;

  tableCount?: number | null;

  /** Ingestion confidence from extraction stats (0–1). Null while unknown. */
  globalConfidence?: number | null;

  /** True when confidence is known and below the review threshold. */
  requiresReview?: boolean | null;

  stage: PipelineStageId;

  statusLabel: string;

  statusVariant: PipelineStatusVariant;

  extractionCategory?: BatchExtractionStatusCategory;

  isMapped: boolean;

  isStructureConfirmed: boolean;

  detail?: string | null;

  canConfirmStructure: boolean;

  canContinueMapping: boolean;

  canRunDataQuality: boolean;

  canViewDqxResults: boolean;

  canResolveDqxErrors: boolean;

  dqRowsPassing?: number | null;

  dqRowsWithIssues?: number | null;

  dqTotalRowsChecked?: number | null;

  isDqxRunning: boolean;

  dqxPhase: PipelineDqxPhase;

  isPreviewLoading: boolean;

  uploadPhase?: "uploading" | "uploaded";
}

export interface UploadingPipelineFileModel {
  fileId: string;

  fileName: string;

  schemaName: string;

  status: "uploading" | "uploaded";

  providerId?: string | null;

  uploadedBy?: string | null;
}

export const UNASSIGNED_PROVIDER_LABEL = "Unassigned";

/** Resolve a provider id to a board-card pill label. */
export function resolveProviderLabel(
  providerId: string | null | undefined,
  providerNameById?: Record<string, string>
): string {
  const id = providerId?.trim();
  if (!id) return UNASSIGNED_PROVIDER_LABEL;
  const name = providerNameById?.[id]?.trim();
  return name || id;
}

export interface PipelineBoardModel {
  cards: PipelineFileCardModel[];

  stageCounts: Record<PipelineStageId, number>;

  batchId: string | null;

  totalFiles: number;

  succeededCount: number;

  mappedCount: number;

  isBatchActive: boolean;
}

export const PIPELINE_STAGES: {
  id: PipelineStageId;
  title: string;
  subtitle: string;
}[] = [
  {
    id: "upload",

    title: "Upload",

    subtitle: "Queued - awaiting structure confirm",
  },

  {
    id: "map",

    title: "Map",

    subtitle: "Column mapping · traffic-light tiers",
  },

  {
    id: "validate",

    title: "Validate",

    subtitle: "DQX rules running on mapped data",
  },

  {
    id: "resolve",

    title: "Resolve",

    subtitle: "Manual cleanse of failing cells",
  },

  {
    id: "published",

    title: "Published",

    subtitle: "Written to gold by DQX when all checks pass",
  },
];

function statusVariantForCategory(
  category: BatchExtractionStatusCategory,

  isMapped: boolean,

  isStructureConfirmed: boolean
): PipelineStatusVariant {
  if (isMapped) {
    return "validating";
  }

  if (isStructureConfirmed) {
    return "mapping";
  }

  if (category === "failed" || category === "cancelled") {
    return "errors";
  }
  if (category === "running" || category === "pending") {
    return "queued";
  }
  if (category === "succeeded") {
    return "ready";
  }
  return "queued";
}

function statusLabelForCard(
  category: BatchExtractionStatusCategory,

  isMapped: boolean,

  isStructureConfirmed: boolean,

  stateMessage: string | null
): string {
  if (isMapped) {
    return "Mapped";
  }

  if (isStructureConfirmed) {
    return "Ready to start mapping";
  }

  if (category === "succeeded") {
    return "Awaiting structure confirm";
  }

  if (category === "running") {
    return stateMessage?.replace(/_/g, " ") || "Processing";
  }

  if (category === "pending") {
    return "Processing";
  }

  if (category === "failed") {
    return "Failed";
  }

  if (category === "cancelled") {
    return "Cancelled";
  }

  if (category === "skipped") {
    return "Skipped";
  }

  return category;
}

export function getPipelineFileStage(
  isMapped: boolean,

  isStructureConfirmed: boolean,

  dqxState: PipelineFileDqxState
): PipelineStageId {
  return stageForFile(
    "pending",
    isMapped,
    isStructureConfirmed,
    normalizePipelineDqxState(dqxState)
  );
}

function stageForFile(
  _category: BatchExtractionStatusCategory,

  isMapped: boolean,

  isStructureConfirmed: boolean,

  dqxState: PipelineFileDqxState
): PipelineStageId {
  if (isMapped) {
    if (dqxState.phase === "running") {
      return "validate";
    }

    if (dqxState.phase === "complete") {
      // A hard-failed table with no results still needs attention on Resolve.
      if (dqxHasHardFailedRun(dqxState)) {
        return "resolve";
      }

      // null/undefined counts (summary fetch failed after a successful run)
      // follow the server's default of 0 failing rows, so a passing file
      // isn't stranded in Resolve until the next board-state refresh.
      if ((dqxState.rowsWithIssues ?? 0) === 0) {
        return "published";
      }

      return "resolve";
    }

    // failed / idle mapped → Validate (retry only when nothing is viewable)
    return "validate";
  }

  if (isStructureConfirmed) {
    return "map";
  }

  return "upload";
}

function applyDqxStateToCard(
  card: Omit<
    PipelineFileCardModel,
    | "canRunDataQuality"
    | "canViewDqxResults"
    | "canResolveDqxErrors"
    | "dqRowsPassing"
    | "dqRowsWithIssues"
    | "dqTotalRowsChecked"
    | "isDqxRunning"
    | "dqxPhase"
    | "stage"
    | "statusLabel"
    | "statusVariant"
    | "detail"
  > & {
    baseStatusLabel: string;
    baseStatusVariant: PipelineStatusVariant;
    baseDetail?: string | null;
  },

  dqxState: PipelineFileDqxState
): PipelineFileCardModel {
  dqxState = normalizePipelineDqxState(dqxState);

  const isDqxRunning = card.isMapped && dqxState.phase === "running";

  const isRetrievingResults =
    isDqxRunning && (dqxState.message?.toLowerCase().includes("retrieving") ?? false);

  const canViewDqxResults =
    card.isMapped &&
    !!dqxState.jobId &&
    (dqxState.phase === "complete" ||
      dqxState.phase === "failed" ||
      (isMultiTableDqxState(dqxState) && (dqxState.rowsWithIssues ?? 0) > 0));

  const canResolveDqxErrors =
    card.isMapped &&
    dqxState.phase === "complete" &&
    (dqxState.rowsWithIssues ?? 0) > 0 &&
    !!dqxState.jobId;

  const canRunDataQuality =
    card.isMapped && (dqxState.phase === "idle" || dqxState.phase === "failed");

  let statusLabel = card.baseStatusLabel;

  let statusVariant = card.baseStatusVariant;

  let detail = card.baseDetail;

  const stage = stageForFile(
    card.extractionCategory ?? "pending",

    card.isMapped,

    card.isStructureConfirmed,

    dqxState
  );

  if (card.isMapped) {
    if (isRetrievingResults) {
      statusLabel = "Retrieving DQ results…";

      statusVariant = "validating";
    } else if (dqxState.phase === "running") {
      statusLabel = "Validating";

      statusVariant = "validating";
    } else if (dqxState.phase === "complete") {
      if (stage === "published") {
        statusLabel = "Published to gold";

        statusVariant = "published";
      } else if ((dqxState.rowsWithIssues ?? 0) > 0) {
        statusLabel = "DQ Errors to resolve";

        statusVariant = "errors";
      } else if (dqxHasHardFailedRun(dqxState)) {
        statusLabel = "DQ checks failed";

        statusVariant = "errors";
      } else {
        statusLabel = "DQ checks complete";

        statusVariant = "ready";
      }
    } else if (dqxState.phase === "failed") {
      statusLabel = "DQ checks failed";

      statusVariant = "errors";

      detail = dqxState.error ?? dqxState.message ?? detail;
    }

    if (dqxState.phase === "running" && dqxState.message) {
      detail = dqxState.message;
    }
  }

  return {
    fileId: card.fileId,

    fileName: card.fileName,

    schemaName: card.schemaName,

    providerId: card.providerId,

    providerLabel: card.providerLabel,

    uploadedBy: card.uploadedBy,

    uploaderEmail: card.uploaderEmail,

    rowCount: card.rowCount,

    tableCount: card.tableCount,

    globalConfidence: card.globalConfidence,

    requiresReview: card.requiresReview,

    stage,

    statusLabel,

    statusVariant,

    extractionCategory: card.extractionCategory,

    isMapped: card.isMapped,

    isStructureConfirmed: card.isStructureConfirmed,

    detail,

    canConfirmStructure: card.canConfirmStructure,

    canContinueMapping: card.canContinueMapping,

    canRunDataQuality,

    canViewDqxResults,

    canResolveDqxErrors,

    dqRowsPassing: dqxState.phase === "complete" ? (dqxState.rowsPassing ?? null) : null,

    dqRowsWithIssues: dqxState.phase === "complete" ? (dqxState.rowsWithIssues ?? null) : null,

    dqTotalRowsChecked: dqxState.phase === "complete" ? (dqxState.totalRowsChecked ?? null) : null,

    isDqxRunning,

    dqxPhase: dqxState.phase,

    isPreviewLoading: card.isPreviewLoading,
  };
}

function buildCardBase(params: {
  batchStatus: BatchExtractionStatus | null;

  fileMeta: {
    fileId: string;
    fileName: string;
    providerId?: string | null;
    uploadedBy?: string | null;
    createdAt?: string | null;
  }[];

  schemaNameByFileId: Record<string, string>;

  providerNameById?: Record<string, string>;

  /** oid → email for avatar letter resolution. */
  emailByUserId?: Record<string, string>;

  currentUserId?: string | null;

  currentUserEmail?: string | null;

  mappedFileIds: string[];

  structureConfirmedFileIds: string[];

  previewingFileId: string | null;

  rowCountByFileId?: Record<string, number>;

  tableCountByFileId?: Record<string, number>;

  globalConfidenceByFileId?: Record<string, number>;

  requiresReviewByFileId?: Record<string, boolean>;

  dqxStateByFileId?: Record<string, PipelineFileDqxState>;

  uploadingFiles?: UploadingPipelineFileModel[];

  singleFile?: {
    fileId: string;

    fileName: string;

    category: BatchExtractionStatusCategory;

    stateMessage: string | null;

    isExtracting: boolean;

    providerId?: string | null;

    uploadedBy?: string | null;
  } | null;
}): PipelineFileCardModel[] {
  const mappedSet = new Set(params.mappedFileIds);

  const structureSet = new Set(params.structureConfirmedFileIds);

  const nameById = new Map(params.fileMeta.map((entry) => [entry.fileId, entry.fileName]));

  const providerIdByFileId = new Map(
    params.fileMeta.map((entry) => [entry.fileId, entry.providerId ?? null])
  );

  const uploadedByByFileId = new Map(
    params.fileMeta.map((entry) => [entry.fileId, entry.uploadedBy ?? null])
  );

  const rowCounts = params.rowCountByFileId ?? {};

  const tableCounts = params.tableCountByFileId ?? {};

  const globalConfidenceByFileId = params.globalConfidenceByFileId ?? {};

  const requiresReviewByFileId = params.requiresReviewByFileId ?? {};

  const dqxStates = params.dqxStateByFileId ?? {};

  const providerNameById = params.providerNameById ?? {};

  const emailOpts = {
    currentUserId: params.currentUserId,
    currentUserEmail: params.currentUserEmail,
    emailByUserId: params.emailByUserId,
  };

  const resolveEmail = (uploadedBy: string | null | undefined) =>
    resolveUploaderEmail(uploadedBy, emailOpts);

  const cards: PipelineFileCardModel[] = [];

  const items = params.batchStatus?.items ?? [];

  if (items.length > 0) {
    for (const item of items) {
      // Extraction-queue rows outlive their file record. fileMeta is the
      // server-scoped catalogue; anything missing from it is an orphan and
      // would otherwise render a card titled with the raw file id.
      if (!nameById.has(item.fileId)) continue;

      const isMapped = mappedSet.has(item.fileId);

      const isStructureConfirmed = structureSet.has(item.fileId);

      const dqxState = getPipelineFileDqxState(dqxStates, item.fileId);

      const providerId = providerIdByFileId.get(item.fileId) ?? null;

      const uploadedBy = uploadedByByFileId.get(item.fileId) ?? null;

      cards.push(
        applyDqxStateToCard(
          {
            fileId: item.fileId,

            fileName: nameById.get(item.fileId) ?? item.fileId,

            schemaName: params.schemaNameByFileId[item.fileId] ?? "—",

            providerId,

            providerLabel: resolveProviderLabel(providerId, providerNameById),

            uploadedBy,

            uploaderEmail: resolveEmail(uploadedBy),

            rowCount: rowCounts[item.fileId] ?? null,

            tableCount: tableCounts[item.fileId] ?? null,

            globalConfidence: globalConfidenceByFileId[item.fileId] ?? null,

            requiresReview: requiresReviewByFileId[item.fileId] ?? null,

            baseStatusLabel: statusLabelForCard(
              item.statusCategory,

              isMapped,

              isStructureConfirmed,

              item.stateMessage
            ),

            baseStatusVariant: statusVariantForCategory(
              item.statusCategory,

              isMapped,

              isStructureConfirmed
            ),

            extractionCategory: item.statusCategory,

            isMapped,

            isStructureConfirmed,

            baseDetail:
              item.triggerError ||
              item.statusError ||
              item.failedTaskMessage ||
              item.skipReason ||
              null,

            canConfirmStructure:
              item.statusCategory === "succeeded" && !isStructureConfirmed && !isMapped,

            canContinueMapping: isStructureConfirmed && !isMapped,

            isPreviewLoading: params.previewingFileId === item.fileId,
          },

          dqxState
        )
      );
    }
  } else if (params.singleFile) {
    const { fileId, fileName, category, stateMessage, isExtracting } = params.singleFile;

    const effectiveCategory: BatchExtractionStatusCategory = isExtracting ? "running" : category;

    const isMapped = mappedSet.has(fileId);

    const isStructureConfirmed = structureSet.has(fileId);

    const dqxState = getPipelineFileDqxState(dqxStates, fileId);

    const providerId = params.singleFile.providerId ?? providerIdByFileId.get(fileId) ?? null;

    const uploadedBy = params.singleFile.uploadedBy ?? uploadedByByFileId.get(fileId) ?? null;

    cards.push(
      applyDqxStateToCard(
        {
          fileId,

          fileName,

          schemaName: params.schemaNameByFileId[fileId] ?? "—",

          providerId,

          providerLabel: resolveProviderLabel(providerId, providerNameById),

          uploadedBy,

          uploaderEmail: resolveEmail(uploadedBy),

          rowCount: rowCounts[fileId] ?? null,

          tableCount: tableCounts[fileId] ?? null,

          globalConfidence: globalConfidenceByFileId[fileId] ?? null,

          requiresReview: requiresReviewByFileId[fileId] ?? null,

          baseStatusLabel: statusLabelForCard(
            effectiveCategory,

            isMapped,

            isStructureConfirmed,

            stateMessage
          ),

          baseStatusVariant: statusVariantForCategory(
            effectiveCategory,

            isMapped,

            isStructureConfirmed
          ),

          extractionCategory: effectiveCategory,

          isMapped,

          isStructureConfirmed,

          baseDetail: stateMessage,

          canConfirmStructure:
            effectiveCategory === "succeeded" && !isStructureConfirmed && !isMapped,

          canContinueMapping: isStructureConfirmed && !isMapped,

          isPreviewLoading: params.previewingFileId === fileId,
        },

        dqxState
      )
    );
  }

  // Spinner while extraction runs; once the file is ready for structure
  // confirm the button itself signals completion, so no indicator.
  for (const card of cards) {
    if (card.stage !== "upload" || card.uploadPhase) continue;
    if (card.extractionCategory === "pending" || card.extractionCategory === "running") {
      card.uploadPhase = "uploading";
    }
  }

  const presentIds = new Set(cards.map((card) => card.fileId));

  for (const uploading of params.uploadingFiles ?? []) {
    if (presentIds.has(uploading.fileId)) continue;

    const providerId = uploading.providerId ?? null;
    const uploadedBy = uploading.uploadedBy ?? null;

    cards.push({
      fileId: uploading.fileId,

      fileName: uploading.fileName,

      schemaName: uploading.schemaName,

      providerId,

      providerLabel: resolveProviderLabel(providerId, providerNameById),

      uploadedBy,

      uploaderEmail: resolveEmail(uploadedBy),

      rowCount: null,

      tableCount: null,

      globalConfidence: null,

      requiresReview: null,

      stage: "upload",

      statusLabel: uploading.status === "uploading" ? "Uploading…" : "Uploaded",

      statusVariant: "queued",

      extractionCategory: "pending",

      isMapped: false,

      isStructureConfirmed: false,

      detail: null,

      canConfirmStructure: false,

      canContinueMapping: false,

      canRunDataQuality: false,

      canViewDqxResults: false,

      canResolveDqxErrors: false,

      dqRowsPassing: null,

      dqRowsWithIssues: null,

      dqTotalRowsChecked: null,

      isDqxRunning: false,

      dqxPhase: "idle",

      isPreviewLoading: false,

      uploadPhase: uploading.status,
    });
  }

  return cards;
}

export function buildPipelineBoardModel(params: {
  batchStatus: BatchExtractionStatus | null;

  fileMeta: {
    fileId: string;
    fileName: string;
    providerId?: string | null;
    uploadedBy?: string | null;
    createdAt?: string | null;
  }[];

  schemaNameByFileId: Record<string, string>;

  providerNameById?: Record<string, string>;

  emailByUserId?: Record<string, string>;

  currentUserId?: string | null;

  currentUserEmail?: string | null;

  mappedFileIds: string[];

  structureConfirmedFileIds: string[];

  previewingFileId: string | null;

  rowCountByFileId?: Record<string, number>;

  tableCountByFileId?: Record<string, number>;

  globalConfidenceByFileId?: Record<string, number>;

  requiresReviewByFileId?: Record<string, boolean>;

  dqxStateByFileId?: Record<string, PipelineFileDqxState>;

  uploadingFiles?: UploadingPipelineFileModel[];

  singleFile?: {
    fileId: string;

    fileName: string;

    category: BatchExtractionStatusCategory;

    stateMessage: string | null;

    isExtracting: boolean;

    providerId?: string | null;

    uploadedBy?: string | null;
  } | null;
}): PipelineBoardModel {
  const items = params.batchStatus?.items ?? [];

  const cards = buildCardBase(params);

  // Oldest first in every column. created_at is a fixed-format timestamp
  // string, so lexicographic comparison is chronological; files the catalogue
  // hasn't stamped yet (fresh uploads, mid-upload placeholders) keep their
  // upload order at the end.
  const createdAtByFileId = new Map<string, string>();
  for (const entry of params.fileMeta) {
    if (entry.createdAt) createdAtByFileId.set(entry.fileId, entry.createdAt);
  }
  cards.sort((a, b) => {
    const aKey = createdAtByFileId.get(a.fileId);
    const bKey = createdAtByFileId.get(b.fileId);
    if (aKey && bKey) return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
    if (aKey) return -1;
    if (bKey) return 1;
    return 0;
  });

  const stageCounts = PIPELINE_STAGES.reduce(
    (acc, stage) => {
      acc[stage.id] = cards.filter((card) => card.stage === stage.id).length;

      return acc;
    },

    {} as Record<PipelineStageId, number>
  );

  return {
    cards,

    stageCounts,

    batchId: params.batchStatus?.batchId ?? null,

    // Cards, not batchStatus.totalFiles — the latter counts orphaned queue rows.
    totalFiles: cards.length,

    succeededCount:
      params.batchStatus?.succeededCount ??
      (cards.some((c) => c.extractionCategory === "succeeded") ? 1 : 0),

    mappedCount: params.mappedFileIds.length,

    isBatchActive: (params.batchStatus?.totalFiles ?? 0) > 1 || items.length > 1,
  };
}
